// Pre-collab messaging, creator side: the pure rules behind the thread's offer cards, its ••• menu
// and whether the creator may write into a thread that has no document yet. No React, no router —
// the page wires the results to navigation.
import type { Campaign, Collab, CollabStatus, GetAvailableSpotsResponseDto } from '@foodfluence/shared';
import type { TimestampLike } from 'utils/dateUtils';
import { hasRecentExtensionRequest, hasRecentWaitlistRequest, isCampaignEnded, isPreAcceptance } from 'utils/collabUtils';
import { canCounter } from 'features/collabs/utils/counterUtils';
import { isCampaignFull } from 'features/collabs/utils/collabUtils';
import { millisOf } from './utils';

/**
 * A brand counter-back as the dashboard writes it to `collab.clientCounters`. Not in this repo's
 * shared `Collab` yet (the dashboard's shared type has it as `ClientCollabCounter`), so it is read
 * through this narrow shape.
 */
interface ClientCounterEntry {
  cash?: number;
  credit?: number;
  deliverableCounts?: Record<string, number>;
  timestamp?: TimestampLike;
  note?: string;
}

export type OfferCardKind = 'invite' | 'proposal' | 'creatorCounter' | 'brandCounter';

/**
 * Whose move it is on the current open offer. `respond`: the creator must answer (Accept / Counter /
 * Decline). `awaitingBrand`: the creator's counter or proposal is with the brand — status only.
 */
export type OfferCardTurn = 'respond' | 'awaitingBrand';

/** Which side of the thread a card sits on — the side of whoever made that offer, like a bubble. */
export type OfferCardSide = 'creator' | 'brand';

export type OfferCardStatus = 'open' | 'replaced' | 'accepted' | 'declined' | 'expired' | 'canceled';

export interface OfferCard {
  key: string;
  kind: OfferCardKind;
  timeMs: number;
  cash?: number;
  credit?: number;
  deliverableCounts?: Record<string, number>;
  note?: string;
  status: OfferCardStatus;
  turn?: OfferCardTurn;
  /** A brand offer re-presented after the brand declined the creator's counter — it still stands. */
  standing?: boolean;
}

const ACCEPTED: CollabStatus[] = [
  'pendingShipment', 'pendingSubmission', 'pendingResubmission', 'pendingVerification',
  'pendingRedemption', 'collabRedeemed', 'collabCompleted', 'pendingDraftSubmission',
];

/** What became of the latest offer, from the collab's status. */
function latestOfferStatus(status: CollabStatus): OfferCardStatus {
  if (isPreAcceptance({ status } as Collab)) return 'open';
  if (ACCEPTED.includes(status)) return 'accepted';
  if (status === 'collabRejected' || status === 'clientRejected') return 'declined';
  if (status === 'collabExpired') return 'expired';
  return 'canceled';
}

/** Whose move it is on the current open offer, from the collab status. */
function turnFor(kind: OfferCardKind, status: CollabStatus): OfferCardTurn | undefined {
  if (status === 'pendingInfluencerApproval' && offerCardSide(kind) === 'brand') return 'respond';
  if (status === 'influencerCountered' || status === 'pendingClientApproval') return 'awaitingBrand';
  return undefined;
}

export function offerCardSide(kind: OfferCardKind): OfferCardSide {
  return kind === 'invite' || kind === 'brandCounter' ? 'brand' : 'creator';
}

/**
 * The offer cards for a campaign thread, oldest first, derived from the collab at read time —
 * nothing is stored as a message, so a card can't be missed, duplicated or go stale.
 *
 * - Opening offer: a brand invite (`collabReceivedTime`) or the creator's proposal
 *   (`collabRequestedTime`). Once anyone has countered, the collab's own terms have been
 *   overwritten, so the invite falls back to the campaign's terms and the proposal to none.
 * - `counters[]` are the creator's counters, `clientCounters[]` the brand's counter-backs. The
 *   latest of each falls back to the collab-level note when the entry has none.
 *
 * Accepted threads with no negotiation get no cards — an "Invite" card at the top of every old
 * thread would be noise.
 */
export function deriveOfferCards(collab: Collab | undefined, campaign?: Campaign): OfferCard[] {
  if (!collab) return [];
  const creatorCounters = collab.counters ?? [];
  const clientCounters = ((collab as Collab & { clientCounters?: ClientCounterEntry[] }).clientCounters) ?? [];
  const negotiated = creatorCounters.length + clientCounters.length > 0;
  if (!isPreAcceptance(collab) && !negotiated) return [];

  const cards: Omit<OfferCard, 'status' | 'turn'>[] = [];

  const isProposal = !!collab.collabRequestedTime && !collab.collabReceivedTime;
  const openedAt = collab.collabReceivedTime ?? collab.collabRequestedTime ?? collab.logs?.[0]?.timestamp;
  if (isProposal) {
    cards.push({
      key: 'opening',
      kind: 'proposal',
      timeMs: millisOf(openedAt),
      // Only a creator counter overwrites `proposedInfluencer*`; a brand counter leaves them intact.
      ...(creatorCounters.length > 0 ? {} : {
        cash: collab.proposedInfluencerCash,
        credit: collab.proposedInfluencerCredit,
        deliverableCounts: collab.proposedInfluencerDeliverableCounts,
      }),
      note: collab.influencerNote ?? undefined,
    });
  } else {
    cards.push({
      key: 'opening',
      kind: 'invite',
      timeMs: millisOf(openedAt),
      cash: negotiated ? campaign?.cash ?? collab.cash : collab.cash,
      credit: negotiated ? campaign?.credit ?? collab.credit : collab.credit,
      deliverableCounts: negotiated ? campaign?.deliverableCounts ?? collab.deliverableCounts : collab.deliverableCounts,
    });
  }

  creatorCounters.forEach((c, i) => cards.push({
    key: `creator-${i}`,
    kind: 'creatorCounter',
    timeMs: millisOf(c.timestamp),
    cash: c.proposedInfluencerCash,
    credit: c.proposedInfluencerCredit,
    deliverableCounts: c.proposedInfluencerDeliverableCounts,
    note: c.note ?? (i === creatorCounters.length - 1 ? collab.influencerCounterNote ?? undefined : undefined),
  }));

  clientCounters.forEach((c, i) => cards.push({
    key: `brand-${i}`,
    kind: 'brandCounter',
    timeMs: millisOf(c.timestamp),
    cash: c.cash,
    credit: c.credit,
    deliverableCounts: c.deliverableCounts,
    note: c.note ?? (i === clientCounters.length - 1 ? collab.clientCounterNote ?? undefined : undefined),
  }));

  // Stable sort keeps the opening offer first when timestamps are missing.
  const sorted = cards.slice().sort((a, b) => a.timeMs - b.timeMs);
  const last = sorted[sorted.length - 1];

  // Brand declined the creator's counter: the counter is dead and the brand's previous offer stands
  // again. Only a new counter clears the flag, so it is still set after the creator accepts, declines
  // or lets the standing offer expire — that outcome belongs to the standing offer, not the counter.
  // A brand offer to stand may be missing on old docs (brand counters not logged); the counter
  // still reads Declined.
  const brandOffer = sorted.slice().reverse().find((c) => offerCardSide(c.kind) === 'brand');
  if (isCounterDeclineCurrent(collab) && last.kind === 'creatorCounter') {
    const outcome = latestOfferStatus(collab.status);
    return [
      ...sorted.slice(0, -1).map((card) => ({ ...card, status: 'replaced' as const })),
      { ...last, status: 'declined' as const },
      ...(brandOffer ? [{
        ...brandOffer,
        key: 'standing',
        timeMs: Math.max(millisOf(collab.collabCounteredTime), last.timeMs + 1),
        status: outcome,
        turn: outcome === 'open' ? ('respond' as const) : undefined,
        standing: true,
      }] : []),
    ];
  }

  return sorted.map((card, i) => {
    if (i < sorted.length - 1) return { ...card, status: 'replaced' as const };
    const status = latestOfferStatus(collab.status);
    return { ...card, status, turn: status === 'open' ? turnFor(card.kind, collab.status) : undefined };
  });
}

/**
 * Whether `businessRejectedCounter` describes the creator's LATEST counter. Counters written before
 * `counterCollab` cleared the flag left it stale: never while a counter is open, and on a terminal
 * collab only if `logs` show the decline (`pendingInfluencerApproval`) after the last
 * `influencerCountered`. A terminal collab with no logs falls back to the flag alone.
 */
export function isCounterDeclineCurrent(collab: Pick<Collab, 'status' | 'businessRejectedCounter' | 'logs'>): boolean {
  if (!collab.businessRejectedCounter) return false;
  if (collab.status === 'pendingInfluencerApproval') return true;
  if (isPreAcceptance(collab as Collab)) return false;
  const statuses = (collab.logs ?? []).map((l) => l?.status);
  if (!statuses.length) return true;
  return statuses.lastIndexOf('pendingInfluencerApproval') > statuses.lastIndexOf('influencerCountered');
}

/** Cards that belong after one message and up to the next, for merging into the thread timeline. */
export function cardsBetween(cards: OfferCard[], afterMs: number, uptoMs: number): OfferCard[] {
  return cards.filter((c) => c.timeMs > afterMs && c.timeMs <= uptoMs);
}

/**
 * Whether the creator may send into a campaign thread whose document doesn't exist yet. The
 * backend creates it only for an open invite; anything else would be refused, so the composer
 * says so instead of failing on send.
 */
export function canCreatorStartThread(collab: Collab | undefined): boolean {
  return collab?.status === 'pendingInfluencerApproval';
}

export interface ThreadMenuActions {
  sendCounter: boolean;
  editProposal: boolean;
  archive: 'archive' | 'unarchive' | null;
}

/**
 * The pre-collab items in the thread's ••• menu. Terms change only through the existing screens,
 * and only while the creator has something open: an invite (Send counter — within the campaign's
 * counter allowance) or their own proposal (Edit proposal). Archive is for campaign threads that
 * exist; marketplace orders have their own store and flows.
 */
export function getThreadMenuActions(params: {
  collab?: Collab;
  campaign?: Campaign;
  isMarketplace: boolean;
  threadExists: boolean;
  isArchived: boolean;
  /** Counter is offered only while the campaign can still take the offer. Absent = open. */
  availability?: CreatorOfferAvailability;
}): ThreadMenuActions {
  const { collab, campaign, isMarketplace, threadExists, isArchived, availability = 'open' } = params;
  const status = collab?.status;
  return {
    sendCounter:
      !isMarketplace && status === 'pendingInfluencerApproval' && availability === 'open' && !!campaign && !!canCounter(campaign, collab!),
    editProposal: !isMarketplace && status === 'pendingClientApproval',
    archive: isMarketplace || !collab || !threadExists ? null : isArchived ? 'unarchive' : 'archive',
  };
}

const OFFER_TITLES: Record<OfferCardKind, string> = {
  invite: 'Invite',
  proposal: 'Your proposal',
  creatorCounter: 'Your counter',
  brandCounter: 'Brand counter',
};

/** Card heading, shared by the thread card and the inbox row preview. */
export function offerCardTitle(card: OfferCard, brandName?: string): string {
  if (card.standing) return 'Offer stands';
  if (card.kind === 'brandCounter' && brandName) return `${brandName} countered`;
  return OFFER_TITLES[card.kind];
}

/** "$300 cash + $50 credit", or "" when the offer carries no money. */
export function offerTermsLine(card: Pick<OfferCard, 'cash' | 'credit'>): string {
  const parts: string[] = [];
  if ((card.cash ?? 0) > 0) parts.push(`$${card.cash} cash`);
  if ((card.credit ?? 0) > 0) parts.push(`$${card.credit} credit`);
  return parts.join(' + ');
}

export type CreatorOfferAvailability =
  | 'open' | 'full' | 'ended' | 'fullEnded' | 'waitlistRequested' | 'extensionRequested' | 'spotOpened'
  | 'slotsBooked'
  /** The page is still fetching spots / slots; never returned by getCreatorOfferAvailability. */
  | 'checking';

/**
 * Whether the creator can still take the open offer. Accepting a full or ended campaign lands in
 * ReviewCollabPage's waitlist / extension modal, so the card says so up front. Evaluated as if the
 * offer were with the creator (the helpers only count `pendingInfluencerApproval` as unconfirmed),
 * so a counter awaiting the brand can show the same warning. `spots` unknown reads as not full.
 * `slotsBooked`: an open-hours in-store campaign with no bookable time slot left
 * (fetchSchedulingCapacityStatus); accepting then also needs an extension.
 */
export function getCreatorOfferAvailability(
  collab: Collab | undefined,
  campaign: Campaign | undefined,
  spots?: GetAvailableSpotsResponseDto,
  slotsBooked = false,
): CreatorOfferAvailability {
  if (!collab || !campaign) return 'open';
  const asOpen = { ...collab, status: 'pendingInfluencerApproval' } as Collab;
  const full = !!spots && spots.maxCollabs != null && isCampaignFull(campaign, spots.availableSpots, asOpen);
  const ended = isCampaignEnded(campaign, asOpen);
  // ExtensionRequestModal blocks on either recent request, whichever kind the creator made.
  if ((full || ended || slotsBooked) && (hasRecentWaitlistRequest(collab) || hasRecentExtensionRequest(collab))) {
    return full && !slotsBooked ? 'waitlistRequested' : 'extensionRequested';
  }
  if (ended) return full ? 'fullEnded' : 'ended';
  // Booked-out slots outrank full, as on ReviewCollabPage; a held spot can't be used either.
  if (slotsBooked) return 'slotsBooked';
  // Not in this repo's shared `Collab` yet (the dashboard's has it). Checked before `full`: the
  // inbox list fetches spots without the collab id, so this creator's reserved spot reads as taken.
  const approvedMs = millisOf((collab as Collab & { waitlistApprovedTime?: TimestampLike }).waitlistApprovedTime);
  if (
    approvedMs > 0 &&
    Date.now() - approvedMs < WAITLIST_RESERVATION_MS &&
    millisOf(collab.waitlistRequestTime) <= approvedMs
  ) return 'spotOpened';
  return full ? 'full' : 'open';
}

/** How long an approved waitlist spot is held — dashboard backend CollabService.getReservedSpots. */
const WAITLIST_RESERVATION_MS = 60 * 60 * 1000;

const AVAILABILITY_SUFFIX: Record<CreatorOfferAvailability, string> = {
  open: '',
  full: ' · Campaign full',
  fullEnded: ' · Campaign full',
  ended: ' · Campaign ended',
  waitlistRequested: ' · On waitlist',
  extensionRequested: ' · Extension requested',
  spotOpened: ' · Spot opened',
  slotsBooked: ' · Slots booked',
  checking: '',
};

/**
 * Inbox row preview for the thread's latest offer card. Offers are derived from the collab, not
 * stored as messages, so a thread with a counter and no typed messages would otherwise read
 * "No messages yet".
 */
export function offerPreviewText(card: OfferCard, brandName?: string, availability?: CreatorOfferAvailability): string {
  const terms = offerTermsLine(card);
  const head = `${offerCardTitle(card, brandName)}${terms ? `: ${terms}` : ''}`;
  const suffix = availability ? AVAILABILITY_SUFFIX[availability] : '';
  if (card.turn === 'respond') return `${head} · Review offer${suffix}`;
  // A reserved spot only matters while the creator can accept it.
  if (card.turn === 'awaitingBrand') return `${head} · Awaiting brand${availability === 'spotOpened' ? '' : suffix}`;
  if (card.status === 'declined') return `${head} · Declined`;
  if (card.status === 'expired') return `${head} · Expired`;
  if (card.status === 'canceled') return `${head} · Canceled`;
  return head;
}

/**
 * When the thread's latest offer happened (ms), or 0 when there's no live offer. Offers aren't
 * messages, so the inbox uses this to date and order threads whose latest activity is an offer.
 */
export function latestOfferActivityMs(collab: Collab | undefined, campaign?: Campaign): number {
  const last = collab ? deriveOfferCards(collab, campaign).at(-1) : undefined;
  return last && last.status !== 'accepted' ? last.timeMs : 0;
}

